import { useState, useRef, useEffect, useCallback } from "react";
import Moment from "react-moment";
import {
  ReplyIcon,
  ForwardIcon,
  DeleteIcon,
  SingleCheckIcon,
  DoubleCheckIcon,
  SpeakerIcon,
  DownloadIcon,
} from "../../../svg/chat";
import AudioPlayer from "./AudioPlayer";
import MediaViewer from "./MediaViewer";
import "./style.css";

// WhatsApp-style quick reactions
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

export default function MessageBubble({ message, isMine, isGroup, onReply, onReact, onDelete, user }) {
  const [showActions, setShowActions] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [translation] = useState(null);
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const reactionPickerRef = useRef(null);
  const longPressTimerRef = useRef(null);

  // Close reaction picker on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target)) {
        setShowReactionPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Long press handlers for mobile
  const handleTouchStart = () => {
    longPressTimerRef.current = setTimeout(() => {
      setShowReactionPicker(true);
    }, 500); // 500ms long press
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  };

  // Double tap to react with like
  const lastTapRef = useRef(0);
  const handleDoubleTap = () => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // Double tap - react with like
      if (onReact) {
        onReact(message._id, "👍");
      }
    }
    lastTapRef.current = now;
  };

  const handleReaction = (emoji) => {
    if (onReact) {
      onReact(message._id, emoji);
    }
    setShowReactionPicker(false);
  };

  const handleRemoveReaction = () => {
    if (onReact) {
      onReact(message._id, null); // null removes the reaction
    }
  };

  const handleDelete = (forEveryone) => {
    if (onDelete) {
      onDelete(message._id, forEveryone);
    }
  };

  // Check if current user has reacted
  const userReaction = message.reactions?.find(
    (r) => r.user === user?.id || r.user?._id === user?.id
  );

  // Download media handler
  const handleDownloadMedia = useCallback(async (url, type, index) => {
    try {
      let downloadUrl = url;

      // For Cloudinary URLs, add fl_attachment to force download
      if (downloadUrl.includes('cloudinary.com')) {
        if (downloadUrl.includes('/image/upload/')) {
          downloadUrl = downloadUrl.replace('/image/upload/', '/image/upload/fl_attachment/');
        } else if (downloadUrl.includes('/video/upload/')) {
          downloadUrl = downloadUrl.replace('/video/upload/', '/video/upload/fl_attachment/');
        }
      }

      // Fetch the file as blob for proper download
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      // Create object URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      // Generate filename
      const extension = type === "video" ? "mp4" : "jpg";
      const filename = `${type}_${Date.now()}_${index}.${extension}`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        document.body.removeChild(link);
      }

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: open in new tab
      window.open(url, "_blank");
    }
  }, []);

  if (message.deletedForEveryone) {
    return (
      <div className={`message_bubble ${isMine ? "mine" : "theirs"} deleted`}>
        <div className="message_content">
          <span className="deleted_text">
            🚫 This message was deleted
          </span>
        </div>
      </div>
    );
  }

  const getSystemMessageText = (msg) => {
    const action = msg.systemMessage?.action;
    const senderName = `${msg.sender?.first_name || "Alguém"}`;
    const targetName = msg.systemMessage?.targetUser?.first_name || "";
    const newValue = msg.systemMessage?.newValue;

    switch (action) {
      case "created_group":
        return `${senderName} criou o grupo`;
      case "added_member":
        return targetName
          ? `${senderName} adicionou ${targetName}`
          : `${senderName} adicionou um novo membro`;
      case "removed_member":
        return targetName
          ? `${senderName} removeu ${targetName}`
          : `${senderName} removeu um membro`;
      case "left_group":
        return `${senderName} saiu do grupo`;
      case "made_admin":
        return targetName
          ? `${targetName} agora é admin`
          : `Um membro agora é admin`;
      case "removed_admin":
        return targetName
          ? `${targetName} não é mais admin`
          : `Um admin foi removido`;
      case "changed_name":
        return newValue
          ? `${senderName} alterou o nome do grupo para "${newValue}"`
          : `${senderName} alterou o nome do grupo`;
      case "changed_picture":
        return `${senderName} alterou a foto do grupo`;
      case "changed_description":
        return `${senderName} alterou a descrição do grupo`;
      default:
        return "Mensagem do sistema";
    }
  };

  if (message.type === "system") {
    return (
      <div className="system_message">
        <span>{getSystemMessageText(message)}</span>
      </div>
    );
  }

  const handleTextToSpeech = () => {
    if ("speechSynthesis" in window && message.text) {
      const utterance = new SpeechSynthesisUtterance(message.text);
      utterance.lang = "pt-PT";
      window.speechSynthesis.speak(utterance);
    }
  };

  const getMessageStatus = () => {
    if (!isMine) return null;

    if (message.readBy?.length > 0) {
      return <DoubleCheckIcon color="#53bdeb" />;
    }
    if (message.deliveredTo?.length > 0) {
      return <DoubleCheckIcon color="#65676b" />;
    }
    return <SingleCheckIcon color="#65676b" />;
  };

  const renderMedia = () => {
    switch (message.type) {
      case "image":
        return (
          <div className="message_media image_grid">
            {message.media?.map((img, index) => (
              <div key={index} className="media_item_wrapper">
                <img
                  src={img.url}
                  alt="Shared image"
                  onClick={() => {
                    setMediaViewerIndex(index);
                    setShowMediaViewer(true);
                  }}
                />
                <button
                  className="media_download_btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadMedia(img.url, "image", index);
                  }}
                  title="Baixar imagem"
                >
                  <DownloadIcon color="#fff" />
                </button>
              </div>
            ))}
          </div>
        );

      case "video":
        return (
          <div className="message_media">
            {message.media?.map((vid, index) => (
              <div key={index} className="video_wrapper">
                <video
                  src={vid.url}
                  onClick={() => {
                    setMediaViewerIndex(index);
                    setShowMediaViewer(true);
                  }}
                />
                <div
                  className="play_overlay"
                  onClick={() => {
                    setMediaViewerIndex(index);
                    setShowMediaViewer(true);
                  }}
                >
                  <svg viewBox="0 0 24 24" width="48" height="48">
                    <path fill="white" d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <button
                  className="media_download_btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadMedia(vid.url, "video", index);
                  }}
                  title="Baixar vídeo"
                >
                  <DownloadIcon color="#fff" />
                </button>
              </div>
            ))}
          </div>
        );

      case "audio":
        return (
          <div className="message_audio">
            {message.media?.map((aud, index) => (
              <AudioPlayer key={index} src={aud.url} isMine={isMine} />
            ))}
          </div>
        );

      case "document":
        return (
          <div className="message_document">
            {message.media?.map((doc, index) => {
              // Ensure document URL has fl_attachment flag for proper download
              let downloadUrl = doc.url;
              if (downloadUrl && downloadUrl.includes('/raw/upload/') && !downloadUrl.includes('fl_attachment')) {
                downloadUrl = downloadUrl.replace('/raw/upload/', '/raw/upload/fl_attachment/');
              }
              return (
                <a
                  key={index}
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="document_link"
                  download={doc.name}
                >
                  <span className="doc_icon">📄</span>
                  <div className="doc_info">
                    <span className="doc_name">{doc.name || "Document"}</span>
                    <span className="doc_size">
                      {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ""}
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        );

      case "location":
        return (
          <div className="message_location">
            <a
              href={`https://www.google.com/maps?q=${message.location?.latitude},${message.location?.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="location_preview">
                <span className="location_icon">📍</span>
                <span>{message.location?.address || "View Location"}</span>
              </div>
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  // Get sender name for group messages
  const getSenderName = () => {
    if (!isGroup || isMine) return null;
    return `${message.sender?.first_name || ""} ${message.sender?.last_name || ""}`.trim();
  };

  const senderName = getSenderName();

  return (
    <div
      className={`message_bubble ${isMine ? "mine" : "theirs"}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchEnd}
      onClick={handleDoubleTap}
    >
      {/* Sender name for group messages */}
      {senderName && (
        <span className="message_sender_name">{senderName}</span>
      )}

      {/* Reply preview */}
      {message.replyTo && (
        <div className="reply_preview">
          <div className="reply_bar"></div>
          <div className="reply_content">
            <span className="reply_sender">
              {message.replyTo.sender?.first_name || "Unknown"}
            </span>
            <span className="reply_text">
              {message.replyTo.type !== "text"
                ? `📎 ${message.replyTo.type}`
                : message.replyTo.text?.substring(0, 50)}
            </span>
          </div>
        </div>
      )}

      {/* Forwarded label */}
      {message.forwardedFrom && (
        <div className="forwarded_label">
          <ForwardIcon color="#65676b" /> Forwarded
        </div>
      )}

      {/* Media content */}
      {renderMedia()}

      {/* Text content */}
      {message.text && (
        <div className="message_text">
          <p>{message.text}</p>
          {translation && (
            <p className="translated_text">
              <em>Translation: {translation}</em>
            </p>
          )}
        </div>
      )}

      {/* Message footer */}
      <div className="message_footer">
        <span className="message_time">
          <Moment format="HH:mm">{message.createdAt}</Moment>
        </span>
        {getMessageStatus()}
      </div>

      {/* Reactions display */}
      {message.reactions?.length > 0 && (
        <div
          className="message_reactions"
          onClick={() => setShowReactionPicker(!showReactionPicker)}
        >
          {/* Group reactions by emoji */}
          {Object.entries(
            message.reactions.reduce((acc, r) => {
              acc[r.emoji] = (acc[r.emoji] || 0) + 1;
              return acc;
            }, {})
          ).map(([emoji, count]) => (
            <span key={emoji} className="reaction">
              {emoji}
              {count > 1 && <span className="reaction_count">{count}</span>}
            </span>
          ))}
        </div>
      )}

      {/* Reaction picker - WhatsApp style */}
      {showReactionPicker && (
        <div
          ref={reactionPickerRef}
          className={`reaction_picker ${isMine ? "left" : "right"}`}
        >
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              className={`reaction_option ${userReaction?.emoji === emoji ? "selected" : ""}`}
              onClick={() => handleReaction(emoji)}
            >
              {emoji}
            </button>
          ))}
          {userReaction && (
            <button
              className="reaction_option remove"
              onClick={handleRemoveReaction}
              title="Remove reaction"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Action buttons - WhatsApp style */}
      {showActions && (
        <div className={`message_actions ${isMine ? "left" : "right"}`}>
          <button
            onClick={() => setShowReactionPicker(!showReactionPicker)}
            title="React"
            className="react_btn"
          >
            😊
          </button>
          <button onClick={() => onReply(message)} title="Reply">
            <ReplyIcon />
          </button>
          <button onClick={handleTextToSpeech} title="Listen">
            <SpeakerIcon />
          </button>
          <button title="Forward">
            <ForwardIcon />
          </button>
          {isMine && (
            <button
              title="Delete"
              className="delete_btn"
              onClick={() => handleDelete(true)}
            >
              <DeleteIcon color="#e41e3f" />
            </button>
          )}
        </div>
      )}

      {/* Media Viewer Modal */}
      {showMediaViewer && message.media && (
        <MediaViewer
          media={message.media.map((m) => ({
            ...m,
            type: message.type,
          }))}
          initialIndex={mediaViewerIndex}
          onClose={() => setShowMediaViewer(false)}
        />
      )}
    </div>
  );
}
